import { useId, type ReactNode } from "react";
import { Link, type LinkProps } from "@/lib/router";
import { ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { useT } from "@/lib/i18n";
import { chapterThemes } from "@/data/chapters";
import { topicThemes } from "@/lib/grammar/topics";

/** Presentation-only design primitives for the playful learning experience. */

export const tones = {
  purple: { tile: "from-[#c3b2ff] to-[#7a5cf0]", soft: "bg-[#efe9ff]", ink: "text-[#4b33b8]", bar: "from-[#9d86ff] to-[#6c4ce8]" },
  pink: { tile: "from-[#ffc8d5] to-[#ff7d9c]", soft: "bg-[#ffedf2]", ink: "text-[#b3214a]", bar: "from-[#ff9fb6] to-[#ff6b8f]" },
  mint: { tile: "from-[#cdf8e6] to-[#4fd1a5]", soft: "bg-[#e3f9ef]", ink: "text-[#0d7552]", bar: "from-[#7fe5bf] to-[#2fbf8c]" },
  mango: { tile: "from-[#ffe9ad] to-[#ffb627]", soft: "bg-[#fff4d6]", ink: "text-[#865600]", bar: "from-[#ffd36b] to-[#f5a524]" },
  peach: { tile: "from-[#ffdcc8] to-[#ff9a6a]", soft: "bg-[#ffeee4]", ink: "text-[#a3431a]", bar: "from-[#ffc09c] to-[#ff8a57]" },
  sky: { tile: "from-[#d2f0ff] to-[#5bbcf5]", soft: "bg-[#e4f5ff]", ink: "text-[#0e5c8a]", bar: "from-[#94d8ff] to-[#3aa9ea]" },
  lagoon: { tile: "from-[#c8f4ea] to-[#3fbfa5]", soft: "bg-[#dcf7f1]", ink: "text-[#0c6a5a]", bar: "from-[#8be4d0] to-[#2bb197]" },
  lavender: { tile: "from-[#f1ecff] to-[#c4b4ff]", soft: "bg-[#f4f0ff]", ink: "text-[#4b33b8]", bar: "from-[#c4b4ff] to-[#9b84fb]" },
} as const;
export type Tone = keyof typeof tones;
export const toneCycle: Tone[] = ["purple", "pink", "mint", "mango", "sky", "peach", "lagoon", "lavender"];

/** Visual identity of each grammar topic (presentation only). */
export const topicTheme: Record<string, { tone: Tone; emoji: string; world: string }> = {
  varnamala: { tone: "lagoon", emoji: "🔤", world: "अक्षर नगरी" },
  vartamana: { tone: "mint", emoji: "☀️", world: "पुदीना बगीचा" },
  bhuta: { tone: "peach", emoji: "⏳", world: "समय यान" },
  bhavishya: { tone: "sky", emoji: "🚀", world: "नीला आसमान" },
  ajna: { tone: "mango", emoji: "📣", world: "आम रोमांच" },
  kridanta: { tone: "lavender", emoji: "🛠️", world: "कारीगर कक्ष" },
  vibhakti: { tone: "pink", emoji: "🧩", world: "स्ट्रॉबेरी पहेली" },
  ...topicThemes,
};

/** Visual world for each curriculum chapter (presentation only). */
export const chapterTheme: Record<string, { tone: Tone; emoji: string; world: string; lesson: string }> = {
  parichay: { tone: "sky", emoji: "🏡", world: "शब्द ग्राम", lesson: "🔤" },
  sarvanam: { tone: "mint", emoji: "🌳", world: "शब्दों का वन", lesson: "🙋" },
  vartaman: { tone: "pink", emoji: "🌸", world: "व्याकरण उद्यान", lesson: "🌱" },
  vibhakti: { tone: "peach", emoji: "⛰️", world: "ज्ञान पर्वत", lesson: "🧩" },
  vakya: { tone: "lagoon", emoji: "🌊", world: "वाक्य सरिता", lesson: "💬" },
  agam: { tone: "mango", emoji: "⭐", world: "तारा अकादमी", lesson: "📖" },
  ...chapterThemes,
};

const tileSize = {
  sm: "size-10 rounded-2xl text-xl",
  md: "size-14 rounded-[1.25rem] text-3xl",
  lg: "size-20 rounded-[1.6rem] text-5xl",
  xl: "size-24 rounded-[1.9rem] text-6xl",
};

/** Glossy, 3D-feeling icon tile holding an emoji illustration. */
export function IconTile({
  tone = "purple",
  size = "md",
  className,
  children,
}: {
  tone?: Tone;
  size?: keyof typeof tileSize;
  className?: string;
  children: ReactNode;
}) {
  return (
    <span
      aria-hidden
      className={cn(
        "relative inline-grid shrink-0 place-items-center overflow-hidden bg-gradient-to-br shadow-[inset_0_-6px_0_rgb(0_0_0/0.08),0_10px_20px_-10px_rgb(60_40_140/0.45)]",
        tones[tone].tile,
        tileSize[size],
        className,
      )}
    >
      <span className="pointer-events-none absolute inset-x-2 top-1 h-1/3 rounded-full bg-white/45 blur-[2px]" />
      <span className="relative drop-shadow-[0_3px_2px_rgb(0_0_0/0.18)]">{children}</span>
    </span>
  );
}

export function ProgressBar({
  value,
  max,
  tone = "purple",
  label,
  className,
  thick = false,
}: {
  value: number;
  max: number;
  tone?: Tone;
  label: string;
  className?: string;
  thick?: boolean;
}) {
  const pct = max > 0 ? Math.min(100, Math.max(0, (value / max) * 100)) : 0;
  return (
    <div
      role="progressbar"
      aria-label={label}
      aria-valuemin={0}
      aria-valuemax={max}
      aria-valuenow={value}
      className={cn("overflow-hidden rounded-full bg-black/[0.06]", thick ? "h-3.5" : "h-2.5", className)}
    >
      <div
        className={cn("h-full rounded-full bg-gradient-to-r transition-[width] duration-700 ease-out", tones[tone].bar)}
        style={{ width: `${pct}%` }}
      />
    </div>
  );
}

/** Purple round "go" indicator used at the end of tappable rows. */
export function RoundGo({ className }: { className?: string }) {
  return (
    <span
      aria-hidden
      className={cn(
        "grid size-11 shrink-0 place-items-center rounded-full bg-candy text-white shadow-soft transition-transform group-hover:translate-x-0.5 group-hover:scale-105",
        className,
      )}
    >
      <ChevronRight className="size-5" strokeWidth={3} />
    </span>
  );
}

export function PillTabs<T extends string>({
  options,
  value,
  onChange,
  label,
  className,
}: {
  options: readonly { id: T; label: ReactNode }[];
  value: T;
  onChange: (id: T) => void;
  label: string;
  className?: string;
}) {
  return (
    <div role="group" aria-label={label} className={cn("-mx-1 flex gap-2 overflow-x-auto px-1 pb-2", className)}>
      {options.map((o) => (
        <button
          key={o.id}
          type="button"
          aria-pressed={value === o.id}
          onClick={() => onChange(o.id)}
          className={cn(
            "deva min-h-11 shrink-0 rounded-full px-5 text-sm font-bold transition-all",
            value === o.id
              ? "bg-candy text-white shadow-soft"
              : "bg-card text-foreground shadow-[var(--shadow-card)] hover:-translate-y-0.5 hover:text-primary",
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

export function SectionHeading({
  title,
  to,
  action,
  className,
}: {
  title: ReactNode;
  to?: LinkProps["to"];
  action?: string;
  className?: string;
}) {
  const t = useT();
  return (
    <div className={cn("mb-4 flex items-end justify-between gap-3", className)}>
      <h2 className="deva text-xl font-bold sm:text-2xl">{title}</h2>
      {to && (
        <Link to={to} className="shrink-0 text-sm font-bold text-primary hover:underline">
          {action ?? t("सभी देखें", "See all")}
        </Link>
      )}
    </div>
  );
}

const bannerTone = {
  candy: "bg-candy text-white",
  lagoon: "bg-lagoon-candy text-white",
  dreamy: "bg-dreamy text-foreground",
};

/** Big rounded hero banner with decorative bubbles and an illustration slot. */
export function Banner({
  tone = "candy",
  eyebrow,
  title,
  sub,
  art,
  children,
  className,
}: {
  tone?: keyof typeof bannerTone;
  eyebrow?: ReactNode;
  title: ReactNode;
  sub?: ReactNode;
  art?: ReactNode;
  children?: ReactNode;
  className?: string;
}) {
  return (
    <section
      className={cn(
        "relative isolate overflow-hidden rounded-[2rem] p-6 shadow-soft sm:p-8",
        bannerTone[tone],
        className,
      )}
    >
      <Bubbles />
      <div className="relative z-10 flex flex-col gap-4 sm:flex-row sm:items-center">
        <div className="min-w-0 flex-1">
          {eyebrow && <p className="deva text-sm font-semibold opacity-90">{eyebrow}</p>}
          <h1 className="deva mt-1 text-3xl font-extrabold leading-snug sm:text-4xl">{title}</h1>
          {sub && <p className="deva mt-2 max-w-xl text-base opacity-90">{sub}</p>}
          {children}
        </div>
        {art && <div className="relative mx-auto shrink-0 sm:mx-0">{art}</div>}
      </div>
    </section>
  );
}

export function Bubbles({ className }: { className?: string }) {
  return (
    <span aria-hidden className={cn("pointer-events-none absolute inset-0 -z-0", className)}>
      <span className="absolute -right-10 -top-12 size-44 rounded-full bg-white/15" />
      <span className="absolute -bottom-16 left-1/3 size-40 rounded-full bg-white/10" />
      <span className="absolute left-6 top-4 size-3 rounded-full bg-white/50" />
      <span className="absolute right-1/3 top-8 size-2 rounded-full bg-white/60" />
      <span className="absolute bottom-6 right-10 size-2.5 rounded-full bg-white/50" />
    </span>
  );
}

/** Decorative floating Devanagari letters. Never used for real content. */
export function FloatingLetters({ letters, className }: { letters: string[]; className?: string }) {
  const spots = [
    "right-[6%] top-[8%] text-pink-400",
    "right-[38%] top-[6%] text-amber-400",
    "right-[4%] bottom-[14%] text-emerald-400",
    "right-[36%] bottom-[8%] text-sky-400",
    "left-[46%] top-[2%] text-violet-400",
  ];
  return (
    <span aria-hidden className={cn("pointer-events-none absolute inset-0", className)}>
      {letters.slice(0, spots.length).map((l, i) => (
        <span
          key={l + i}
          className={cn("deva absolute animate-float text-2xl font-bold opacity-60 sm:text-3xl", spots[i])}
          style={{ animationDelay: `${i * 0.8}s` }}
        >
          {l}
        </span>
      ))}
    </span>
  );
}

export function StatBubble({
  label,
  value,
  emoji,
  tone = "purple",
}: {
  label: ReactNode;
  value: ReactNode;
  emoji: string;
  tone?: Tone;
}) {
  return (
    <div className="bubble flex flex-col gap-3 p-4">
      <p className="deva text-sm font-semibold text-muted-foreground">{label}</p>
      <div className="flex items-center justify-between gap-2">
        <p className="font-display text-2xl font-extrabold sm:text-3xl">{value}</p>
        <IconTile tone={tone} size="sm">
          {emoji}
        </IconTile>
      </div>
    </div>
  );
}

export function PlayfulEmptyState({
  title,
  text,
  action,
  mood = "think",
}: {
  title: ReactNode;
  text?: ReactNode;
  action?: ReactNode;
  mood?: MascotMood;
}) {
  return (
    <div className="bubble flex flex-col items-center gap-3 p-8 text-center">
      <Mascot mood={mood} className="size-28" />
      <h2 className="deva text-xl font-bold">{title}</h2>
      {text && <p className="deva max-w-md text-muted-foreground">{text}</p>}
      {action}
    </div>
  );
}

/** Deterministic celebratory confetti (SSR-safe, decorative). */
export function Confetti({ count = 26 }: { count?: number }) {
  const colors = ["#ff7d9c", "#ffc857", "#8de5c2", "#a78bfa", "#94d8ff", "#ffb38a"];
  return (
    <span aria-hidden className="pointer-events-none absolute inset-x-0 top-0 h-80 overflow-hidden">
      {Array.from({ length: count }, (_, i) => (
        <span
          key={i}
          className="confetti-piece absolute top-0 block rounded-sm"
          style={{
            left: `${(i * 37) % 100}%`,
            width: 8 + (i % 3) * 3,
            height: 12 + (i % 2) * 6,
            background: colors[i % colors.length],
            animationDelay: `${(i % 9) * 0.12}s`,
          }}
        />
      ))}
    </span>
  );
}

export type MascotMood = "wave" | "cheer" | "happy" | "think";

/**
 * बेरी — the platform's original blueberry learning buddy.
 * Pure SVG, decorative unless a label is given.
 */
export function Mascot({
  mood = "happy",
  holding,
  className,
  label,
}: {
  mood?: MascotMood;
  holding?: "book" | "star" | "trophy";
  className?: string;
  label?: string;
}) {
  const raw = useId();
  const id = raw.replace(/[^a-zA-Z0-9]/g, "");
  const leftArm =
    mood === "cheer" ? "rotate(-140 52 120)" : mood === "think" ? "rotate(-20 52 120)" : "rotate(20 52 120)";
  const rightArm =
    mood === "wave" || mood === "cheer" ? "rotate(140 148 120)" : holding ? "rotate(-60 148 120)" : "rotate(-20 148 120)";
  return (
    <svg
      viewBox="0 0 200 200"
      className={cn("select-none", className)}
      role={label ? "img" : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
    >
      <defs>
        <radialGradient id={`${id}b`} cx="38%" cy="32%" r="75%">
          <stop offset="0%" stopColor="#b9a6ff" />
          <stop offset="55%" stopColor="#8466f4" />
          <stop offset="100%" stopColor="#5a3ad8" />
        </radialGradient>
        <linearGradient id={`${id}l`} x1="0" x2="1" y1="0" y2="1">
          <stop offset="0%" stopColor="#a8f0d2" />
          <stop offset="100%" stopColor="#36b98a" />
        </linearGradient>
      </defs>
      <ellipse cx="100" cy="188" rx="52" ry="7" fill="#3b2f7a" opacity="0.12" />
      {/* feet */}
      <ellipse cx="78" cy="176" rx="16" ry="9" fill="#5a3ad8" />
      <ellipse cx="122" cy="176" rx="16" ry="9" fill="#5a3ad8" />
      {/* arms */}
      <g transform={leftArm}>
        <ellipse cx="40" cy="120" rx="13" ry="22" fill="#6f50ea" />
      </g>
      <g transform={rightArm}>
        <g className={mood === "wave" ? "origin-[148px_120px] animate-wiggle" : undefined}>
          <ellipse cx="160" cy="120" rx="13" ry="22" fill="#6f50ea" />
        </g>
      </g>
      {/* body */}
      <circle cx="100" cy="112" r="68" fill={`url(#${id}b)`} />
      <ellipse cx="74" cy="76" rx="22" ry="13" fill="#fff" opacity="0.28" transform="rotate(-25 74 76)" />
      {/* blueberry crown + leaf */}
      <path d="M86 48 L92 40 L100 47 L108 40 L114 48 Z" fill="#4a2fc0" />
      <path d="M100 44 C96 24 112 14 128 18 C126 34 114 44 100 44 Z" fill={`url(#${id}l)`} />
      <path d="M102 42 C108 32 116 26 124 22" stroke="#1f8f68" strokeWidth="2" fill="none" strokeLinecap="round" />
      {/* eyes */}
      <ellipse cx="78" cy="108" rx="15" ry="17" fill="#fff" />
      <ellipse cx="122" cy="108" rx="15" ry="17" fill="#fff" />
      <circle cx={mood === "think" ? 82 : 80} cy={mood === "think" ? 104 : 110} r="9" fill="#2a2150" />
      <circle cx={mood === "think" ? 126 : 124} cy={mood === "think" ? 104 : 110} r="9" fill="#2a2150" />
      <circle cx="83" cy="105" r="3.2" fill="#fff" />
      <circle cx="127" cy="105" r="3.2" fill="#fff" />
      {/* cheeks */}
      <ellipse cx="62" cy="132" rx="9" ry="5.5" fill="#ff8fb0" opacity="0.75" />
      <ellipse cx="138" cy="132" rx="9" ry="5.5" fill="#ff8fb0" opacity="0.75" />
      {/* mouth */}
      {mood === "think" ? (
        <path d="M90 140 Q100 136 110 140" stroke="#2a2150" strokeWidth="4" fill="none" strokeLinecap="round" />
      ) : (
        <>
          <path d="M84 132 Q100 156 116 132 Z" fill="#2a2150" />
          <path d="M92 142 Q100 150 108 142 Q100 138 92 142 Z" fill="#ff7d9c" />
        </>
      )}
      {holding === "star" && (
        <path
          d="M168 60 l7 14 15 2 -11 10 3 15 -14 -7 -14 7 3 -15 -11 -10 15 -2 z"
          fill="#ffc857"
          stroke="#f5a524"
          strokeWidth="2"
          strokeLinejoin="round"
        />
      )}
      {holding === "trophy" && (
        <g transform="translate(150 66)">
          <path d="M6 0 h28 v12 a14 14 0 0 1 -28 0 z" fill="#ffc857" stroke="#e59a12" strokeWidth="2" />
          <path d="M6 4 h-6 a8 8 0 0 0 8 10 M34 4 h6 a8 8 0 0 1 -8 10" stroke="#e59a12" strokeWidth="2.5" fill="none" />
          <rect x="16" y="25" width="8" height="8" fill="#e59a12" />
          <rect x="10" y="32" width="20" height="6" rx="2" fill="#f5a524" />
        </g>
      )}
      {holding === "book" && (
        <g transform="translate(64 146)">
          <path d="M0 4 Q18 -2 36 4 L36 30 Q18 24 0 30 Z" fill="#ff7d9c" />
          <path d="M36 4 Q54 -2 72 4 L72 30 Q54 24 36 30 Z" fill="#ff9fb6" />
          <path d="M36 4 V30" stroke="#d9476c" strokeWidth="2" />
        </g>
      )}
    </svg>
  );
}
